import { createTheme, Theme } from "@mui/material/styles";

import { PaletteColorOptions, PaletteMode } from "@mui/material";

import { getDxAiPatientClinicMuiAppBar } from "./components/MuiAppBar";
import { getDxAiPatientClinicMuiButton } from "./components/MuiButton";
import { getDxAiPatientClinicMuiCard } from "./components/MuiCard";
import { getDxAiPatientClinicMuiDialog } from "./components/MuiDialog";
import { getDxAiPatientClinicMuiDialogTitle } from "./components/MuiDialogTitle";
import { getDxAiPatientClinicMuiDialogContent } from "./components/MuiDialogContent";
import { getDxAiPatientClinicMuiDialogActions } from "./components/MuiDialogActions";
import { getDxAiPatientClinicMuiDivider } from "./components/MuiDivider";
import { getDxAiPatientClinicMuiTypography } from "./components/MuiTypography";
import { getDxAiPatientClinicMuiAccordion } from "./components/MuiAccordion";
import { getDxAiPatientClinicMuiAccordionSummary } from "./components/MuiAccordionSummary";
import { getDxAiPatientClinicMuiAccordionDetails } from "./components/MuiAccordionDetails";
import { getDxAiPatientClinicMuiTab } from "./components/MuiTab";
import { getTypography } from "./CustomTypography";
import eiraToken from "../../tokens/eira-color-scheme.json";
import { getDxAiPatientClinicMuiTextField } from "./components/MuiTextField";
import { getDxAiPatientClinicMuiPopover } from "./components/MuiPopover";
import { getPalletes, getPatientPallete } from "./CustomPalette";
import { prodClient } from "../constants";

declare module "@mui/material/styles" {
  interface TypographyVariants {
    displayLarge: React.CSSProperties;
    displayMedium: React.CSSProperties;
    displaySmall: React.CSSProperties;
    headingLarge: React.CSSProperties;
    headingMedium: React.CSSProperties;
    headingSmall: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    displayLarge: React.CSSProperties;
    displayMedium: React.CSSProperties;
    displaySmall: React.CSSProperties;
    headingLarge: React.CSSProperties;
    headingMedium: React.CSSProperties;
    headingSmall: React.CSSProperties;
    titleLarge: React.CSSProperties;
    titleMedium: React.CSSProperties;
    titleSmall: React.CSSProperties;
    bodyLarge: React.CSSProperties;
    bodyMedium: React.CSSProperties;
    bodySmall: React.CSSProperties;
    labelLarge: React.CSSProperties;
    labelMedium: React.CSSProperties;
    labelSmall: React.CSSProperties;
  }

  interface Palette {
    tertiary: PaletteColorOptions;
    onPrimary: PaletteColorOptions;
    onSecondary: PaletteColorOptions;
    onTertiary: PaletteColorOptions;
    onError: PaletteColorOptions;
    primaryContainer: PaletteColorOptions;
    secondaryContainer: PaletteColorOptions;
    tertiaryContainer: PaletteColorOptions;
    errorContainer: PaletteColorOptions;
    onPrimaryContainer: PaletteColorOptions;
    onSecondaryContainer: PaletteColorOptions;
    onTertiaryContainer: PaletteColorOptions;
    onErrorContainer: PaletteColorOptions;
    surface: PaletteColorOptions;
    onSurface: PaletteColorOptions;
    surfaceVariant: PaletteColorOptions;
    onSurfaceVariant: PaletteColorOptions;
    outline: PaletteColorOptions;
    outlineVariant: PaletteColorOptions;
    onBackground: PaletteColorOptions;
    primaryFixed: PaletteColorOptions;
    onPrimaryFixed: PaletteColorOptions;

    low: PaletteColorOptions;
    onLow: PaletteColorOptions;
    lowContainer: PaletteColorOptions;
    onLowContainer: PaletteColorOptions;
    moderate: PaletteColorOptions;
    onModerate: PaletteColorOptions;
    moderateContainer: PaletteColorOptions;
    onModerateContainer: PaletteColorOptions;
    high: PaletteColorOptions;
    onHigh: PaletteColorOptions;
    highContainer: PaletteColorOptions;
    onLighContainer: PaletteColorOptions;
  }

  interface PaletteOptions {
    tertiary: PaletteColorOptions;
    onPrimary: PaletteColorOptions;
    onSecondary: PaletteColorOptions;
    onTertiary: PaletteColorOptions;
    onError: PaletteColorOptions;
    primaryContainer: PaletteColorOptions;
    secondaryContainer: PaletteColorOptions;
    tertiaryContainer: PaletteColorOptions;
    errorContainer: PaletteColorOptions;
    onPrimaryContainer: PaletteColorOptions;
    onSecondaryContainer: PaletteColorOptions;
    onTertiaryContainer: PaletteColorOptions;
    onErrorContainer: PaletteColorOptions;
    surface: PaletteColorOptions;
    onSurface: PaletteColorOptions;
    surfaceVariant: PaletteColorOptions;
    onSurfaceVariant: PaletteColorOptions;
    outline: PaletteColorOptions;
    outlineVariant: PaletteColorOptions;
    onBackground: PaletteColorOptions;
    primaryFixed: PaletteColorOptions;
    onPrimaryFixed: PaletteColorOptions;

    low: PaletteColorOptions;
    onLow: PaletteColorOptions;
    lowContainer: PaletteColorOptions;
    onLowContainer: PaletteColorOptions;
    moderate: PaletteColorOptions;
    onModerate: PaletteColorOptions;
    moderateContainer: PaletteColorOptions;
    onModerateContainer: PaletteColorOptions;
    high: PaletteColorOptions;
    onHigh: PaletteColorOptions;
    highContainer: PaletteColorOptions;
    onLighContainer: PaletteColorOptions;
  }
}

// Update the Typography's variant prop options
declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    displayLarge: true;
    displayMedium: true;
    displaySmall: true;
    headingLarge: true;
    headingMedium: true;
    headingSmall: true;
    titleLarge: true;
    titleMedium: true;
    titleSmall: true;
    bodyLarge: true;
    bodyMedium: true;
    bodySmall: true;
    labelLarge: true;
    labelMedium: true;
    labelSmall: true;
  }
}

// Update the Button's color prop options
declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    tertiary: true;
    onPrimary: true;
    onSecondary: true;
    onTertiary: true;
    onError: true;
    primaryContainer: true;
    secondaryContainer: true;
    tertiaryContainer: true;
    errorContainer: true;
    onPrimaryContainer: true;
    onSecondaryContainer: true;
    onTertiaryContainer: true;
    onErrorContainer: true;
    surface: true;
    onSurface: true;
    surfaceVariant: true;
    onSurfaceVariant: true;
    outline: true;
    outlineVariant: true;
    onBackground: true;
    primaryFixed: true;
    onPrimaryFixed: true;

    low: true;
    onLow: true;
    lowContainer: true;
    onLowContainer: true;
    moderate: true;
    onModerate: true;
    moderateContainer: true;
    onModerateContainer: true;
    high: true;
    onHigh: true;
    highContainer: true;
    onLighContainer: true;
  }
}

// Update the Chip's color prop options
declare module "@mui/material/Chip" {
  interface ChipPropsColorOverrides {
    tertiary: true;
    onPrimary: true;
    onSecondary: true;
    onTertiary: true;
    onError: true;
    primaryContainer: true;
    secondaryContainer: true;
    tertiaryContainer: true;
    errorContainer: true;
    onPrimaryContainer: true;
    onSecondaryContainer: true;
    onTertiaryContainer: true;
    onErrorContainer: true;
    surface: true;
    onSurface: true;
    surfaceVariant: true;
    onSurfaceVariant: true;
    outline: true;
    outlineVariant: true;
    onBackground: true;
    primaryFixed: true;
    onPrimaryFixed: true;

    low: true;
    onLow: true;
    lowContainer: true;
    onLowContainer: true;
    moderate: true;
    onModerate: true;
    moderateContainer: true;
    onModerateContainer: true;
    high: true;
    onHigh: true;
    highContainer: true;
    onLighContainer: true;
  }
}

export const generateTheme = (mode: string, user: string): Theme => {
  let token;
  if (user.includes(prodClient)) {
    token = eiraToken;
  }

  let typo, palette, fonts;

  typo = getTypography(token);

  palette = user.includes(prodClient)
    ? getPatientPallete(token, mode)
    : getPalletes(token, mode);

  let muiTxtFld,
    muiButton,
    muiAppbar,
    muiPopOver,
    muiTypo,
    muiCard,
    muiDivider,
    muiDialogTitle,
    muiDialog,
    muiDialogContent,
    muiDialogActions,
    muiAccordion,
    muiAccordionDetails,
    muiAccordionSummary,
    muiPickerStaticWrapper,
    muiPickersToolbar,
    muiButtonBase,
    muiPickerDialogActions,
    muiPcikerClock,
    muiTab;

  if (user.includes(prodClient)) {
    muiTxtFld = getDxAiPatientClinicMuiTextField(palette);
    muiButton = getDxAiPatientClinicMuiButton(palette);
    muiAppbar = getDxAiPatientClinicMuiAppBar(palette);
    muiTypo = getDxAiPatientClinicMuiTypography(palette);
    muiCard = getDxAiPatientClinicMuiCard(palette);
    muiDivider = getDxAiPatientClinicMuiDivider(palette);
    muiDialogTitle = getDxAiPatientClinicMuiDialogTitle(palette);
    muiDialog = getDxAiPatientClinicMuiDialog(palette);
    muiDialogContent = getDxAiPatientClinicMuiDialogContent(palette);
    muiDialogActions = getDxAiPatientClinicMuiDialogActions(palette);
    muiAccordion = getDxAiPatientClinicMuiAccordion(palette);
    muiAccordionDetails = getDxAiPatientClinicMuiAccordionDetails(palette);
    muiAccordionSummary = getDxAiPatientClinicMuiAccordionSummary(palette);
    muiTab = getDxAiPatientClinicMuiTab(palette);
    muiPopOver = getDxAiPatientClinicMuiPopover(palette);
  }

  return createTheme({
    palette: {
      ...palette,
      mode: mode as PaletteMode, // Dynamic mode switching
    },
    typography: {
      ...typo, // Ensure typography is inside the `typography` object
    },
    components: {
      ...muiTxtFld,
      ...muiButton,
      ...muiAppbar,
      ...muiPopOver,
      ...muiTypo,
      ...muiCard,
      ...muiDivider,
      ...muiDialogTitle,
      ...muiDialog,
      ...muiDialogContent,
      ...muiDialogActions,
      ...muiAccordion,
      ...muiAccordionSummary,
      ...muiAccordionDetails,
      ...muiTab,
      // ...muiPickerStaticWrapper,
      // ...muiPickersToolbar,
      // ...muiButtonBase,
      // ...muiPickerDialogActions,
      // ...muiPcikerClock,
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 600,
        md: 900,
        lg: 1200,
        xl: 1536,
      },
    },
  });
};
